"use client";

import { useState, type ReactNode } from "react";
import styles from "./styles.module.scss";
import cn from "clsx";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import TailArrow from "@/SvgIcons/TailArror";
import UnivoLink from "@/components/common/UnivoLink";
import type { FooterNavigationColumn } from "@/features/site-chrome/model/siteChrome.types";

export default function FooterNavigationLinks({
  footerNavigation,
  children,
}: {
  footerNavigation: FooterNavigationColumn[];
  /** Extra content revealed together with the links on "show more". */
  children?: ReactNode;
}) {
  const [showList, setShowList] = useState(false);
 
  const toggleList = () => {
    setShowList(!showList);
    
    pushToDataLayer({
      event: "generic_cta_click",
      cta_text: "Show More",
      section_name: "footer",
      page_type: getGlobalStateValue('pageType'),
    });
  };

  const handleGaClick = (itemData: { ctaText?: string; sectionText?: string }) => {
    const {ctaText="", sectionText="" } = itemData || {};
    pushToDataLayer({
      event : "footer_interaction",
      cta_text : ctaText || "NA",
      section_name: sectionText || "NA",
      page_type : getGlobalStateValue('pageType')
    });
  };

  const firingWebEngage=(source:string,source2:string)=>{
    window?.webengage?.track("Footer Clicked", {
      "Footer Name":source,
      "Sub Category":source2,
    })
  }

  return (
    <>
      <div
        className={cn(
          "w-full flex justify-center items-center",
          styles.readmoreCTA
        )}
      >
        <button
          onClick = {toggleList}
          className={styles.showMoreButton}
          aria-label={showList ? "show less" : "show more"}
          id="footer-show-more"
        >
          {showList ? "show less" : "show more"}
          <TailArrow className={styles.arrowDownIcon} rotate={showList? -180 :0}/>
        </button>

      </div>
      <div
        className={cn(styles.hiddenFooterLinks, {
          [styles.showFooterLinks]: showList,
        })}
      >
        <div className={styles.footerLinksContent}>
          <div
            className={cn(
              styles.hiddenMenuContainer
            )}
          >
            {/* Strapi v5 footer navigation: a column's heading is optional,
                without one its links start at the top of the column. The
                first column's links use the heading style (bold), as before. */}
            {footerNavigation.map((column, columnIndex) => {
              // A group heading (bold, not a link) turns the links after it in
              // this column into normal-weight sub-items.
              const groupHeadingIndex = column.links.findIndex((link) => link.isGroupHeading);
              return (
              <ul
                className={cn(
                  styles[`menu${columnIndex + 1}`],
                  "col-span-1",
                  columnIndex > 0 && "w-fit"
                )}
                key={columnIndex}
              >
                {column.heading && (
                  <li className={styles.headingLink}>{column.heading}</li>
                )}
                {column.links.map((link, index) =>
                  link.isGroupHeading ? (
                  <li
                    className={cn(styles.headingLink, styles.headingLinkWithChild, "w-fit")}
                    key={index}
                  >
                    {link.title}
                  </li>
                  ) : (
                  <li
                    className={cn(
                      groupHeadingIndex >= 0 && index > groupHeadingIndex
                        ? styles.subMenuItem
                        : columnIndex === 0
                          ? styles.headingLink
                          : styles.subMenuItem,
                      "w-fit"
                    )}
                    key={index}
                  >
                    {/* isAnchor: plain <a> (full page load), as before */}
                    <UnivoLink
                      isAnchor
                      href={link.url}
                      target={link.target}
                      onClick={() => {
                        handleGaClick({
                          sectionText:
                            column.heading || (columnIndex === 0 ? link.title : "NA"),
                          ctaText: link.title || "NA",
                        });
                        firingWebEngage(link.title, column.heading || "General");
                      }}
                    >
                      {link.title}
                    </UnivoLink>
                  </li>
                  )
                )}
              </ul>
              );
            })}
          </div>

          {children}

          <hr className="text-[#D4D4D4] stroke-[1.5px]" />
        </div>
      </div>
    </>
  );
}
